/**
 * BaremePanel — the fidélité scale editor. Reads the per-lab bareme (GET /finance/fidelite/bareme,
 * cap finance.fidelite.read; falls back to the config default) and upserts it
 * (PUT /finance/fidelite/bareme, cap finance.fidelite.manage). The tier % IS the discount applied at
 * quote — points are NEVER redeemed for cash. A Recharts visual plots each palier's seuil + remise.
 *
 * 5-state DoD (panel level): chargement/erreur via the query; vide is impossible (the server always
 * returns at least the config default); sans-droit (manage) disables the save; offline/forbidden are
 * owned by the page.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Bar,
  CartesianGrid,
  Legend,
  Line,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Badge, Button, Card, ErrorState, FormField, Input, LoadingState, Table, useToast, type Column } from "@/ui";
import { useCan } from "@/auth";
import { useT } from "@/app";
import { ApiError } from "@/api";
import { token } from "@/design/theme";
import { FINANCE_CAPS, useBareme, usePutBareme } from "../api";
import type { FidelitePalier } from "../types";

export interface BaremePanelProps {
  labId: string | null;
  enabled: boolean;
}

interface DraftPalier extends FidelitePalier {
  _id: string;
}

let seq = 0;
const toDraft = (p: FidelitePalier): DraftPalier => ({ _id: `p_${++seq}`, ...p });

export function BaremePanel({ labId, enabled }: BaremePanelProps) {
  const { t } = useT();
  const toast = useToast();
  const canManage = useCan(FINANCE_CAPS.fideliteManage);

  const query = useBareme(labId, enabled);
  const putMut = usePutBareme(labId);

  const [pointsPerDinar, setPointsPerDinar] = useState<string>("");
  const [paliers, setPaliers] = useState<DraftPalier[]>([]);
  const [touched, setTouched] = useState(false);
  const [isDefault, setIsDefault] = useState(false);

  // Seed the editor from the loaded bareme exactly once per fetch.
  useEffect(() => {
    if (!query.data) return;
    setPointsPerDinar(String(query.data.points_par_dinar ?? 0));
    setPaliers((query.data.paliers ?? []).map(toDraft));
    setIsDefault(!!query.data.default);
  }, [query.data]);

  const ppdValue = pointsPerDinar.trim() === "" ? null : Number(pointsPerDinar.replace(",", "."));
  const ppdInvalid = ppdValue == null || !Number.isFinite(ppdValue) || ppdValue < 0;
  const palierCodeMissing = paliers.some((p) => p.code.trim() === "");
  const noPaliers = paliers.length === 0;
  const valid = !ppdInvalid && !palierCodeMissing && !noPaliers;

  const chartData = useMemo(
    () =>
      [...paliers]
        .sort((a, b) => a.seuilPoints - b.seuilPoints)
        .map((p) => ({
          code: p.label?.trim() || p.code || "—",
          seuil: p.seuilPoints,
          remise: p.remisePct,
        })),
    [paliers],
  );

  const updatePalier = (id: string, patch: Partial<DraftPalier>) =>
    setPaliers((prev) => prev.map((p) => (p._id === id ? { ...p, ...patch } : p)));
  const addPalier = () =>
    setPaliers((prev) => [...prev, toDraft({ code: "", label: "", seuilPoints: 0, remisePct: 0 })]);
  const removePalier = (id: string) => setPaliers((prev) => prev.filter((p) => p._id !== id));

  const save = () => {
    setTouched(true);
    if (!valid || !canManage) return;
    putMut.mutate(
      {
        points_par_dinar: ppdValue ?? 0,
        paliers: paliers.map((p) => ({
          code: p.code.trim(),
          ...(p.label?.trim() ? { label: p.label.trim() } : {}),
          seuilPoints: Math.max(0, Math.trunc(p.seuilPoints)),
          remisePct: Math.max(0, p.remisePct),
        })),
      },
      {
        onSuccess: () => {
          toast.success(t("finance.fid.bareme.success"));
          setIsDefault(false);
          void query.refetch();
        },
        onError: (err) => {
          toast.error(err instanceof ApiError ? err.message : t("finance.fid.bareme.title"));
        },
      },
    );
  };

  if (query.isLoading) return <LoadingState variant="table" rows={4} />;
  if (query.isError) {
    return (
      <ErrorState
        message={query.error.message}
        code={query.error.code}
        onRetry={() => void query.refetch()}
      />
    );
  }

  const columns: ReadonlyArray<Column<DraftPalier>> = [
    {
      key: "code",
      header: t("finance.fid.palier.code"),
      cell: (row) => (
        <Input
          aria-label={t("finance.fid.palier.code")}
          autoComplete="off"
          value={row.code}
          invalid={touched && row.code.trim() === ""}
          disabled={!canManage}
          onChange={(e) => updatePalier(row._id, { code: e.target.value })}
        />
      ),
    },
    {
      key: "label",
      header: t("finance.fid.palier.label"),
      cell: (row) => (
        <Input
          aria-label={t("finance.fid.palier.label")}
          autoComplete="off"
          value={row.label ?? ""}
          disabled={!canManage}
          onChange={(e) => updatePalier(row._id, { label: e.target.value })}
        />
      ),
    },
    {
      key: "seuil",
      header: t("finance.fid.palier.seuil"),
      align: "end",
      width: "140px",
      cell: (row) => (
        <Input
          mono
          className="nv-ltr-island"
          inputMode="numeric"
          aria-label={t("finance.fid.palier.seuil")}
          value={String(row.seuilPoints)}
          disabled={!canManage}
          style={{ textAlign: "end" }}
          onChange={(e) => updatePalier(row._id, { seuilPoints: Number(e.target.value.replace(/\D/g, "")) || 0 })}
        />
      ),
    },
    {
      key: "remise",
      header: t("finance.fid.palier.remise"),
      align: "end",
      width: "120px",
      cell: (row) => (
        <Input
          mono
          className="nv-ltr-island"
          inputMode="decimal"
          aria-label={t("finance.fid.palier.remise")}
          value={String(row.remisePct)}
          disabled={!canManage}
          style={{ textAlign: "end" }}
          onChange={(e) => {
            const n = Number(e.target.value.replace(",", "."));
            updatePalier(row._id, { remisePct: Number.isFinite(n) && n >= 0 ? n : 0 });
          }}
        />
      ),
    },
    {
      key: "actions",
      header: "",
      align: "end",
      width: "56px",
      cell: (row) => (
        <Button
          variant="ghost"
          size="sm"
          aria-label={t("finance.fid.palier.remove")}
          disabled={!canManage || paliers.length <= 1}
          onClick={() => removePalier(row._id)}
        >
          ✕
        </Button>
      ),
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
      <Card
        title={t("finance.fid.bareme.title")}
        actions={
          <Badge tone={isDefault ? "neutral" : "success"}>
            {isDefault ? t("finance.fid.bareme.default") : t("finance.fid.bareme.custom")}
          </Badge>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
          <FormField
            label={t("finance.fid.pointsParDinar")}
            hint={t("finance.fid.pointsParDinar.hint")}
            required
            {...(touched && ppdInvalid ? { error: t("finance.field.intInvalid") } : {})}
          >
            {(p) => (
              <Input
                {...p}
                mono
                className="nv-ltr-island"
                inputMode="decimal"
                autoComplete="off"
                value={pointsPerDinar}
                invalid={touched && ppdInvalid}
                disabled={!canManage}
                style={{ maxWidth: 160, textAlign: "end" }}
                onChange={(e) => setPointsPerDinar(e.target.value)}
              />
            )}
          </FormField>

          <div>
            <div style={{ fontSize: "var(--nv-fs-label)", color: token.slate, marginBottom: "var(--nv-s-2)" }}>
              {t("finance.fid.paliers")}
            </div>
            <Table<DraftPalier>
              columns={columns}
              rows={paliers}
              rowKey={(r) => r._id}
              caption={t("finance.fid.paliers")}
              emptyTitle={t("finance.fid.palier.none")}
            />
            {touched && noPaliers && (
              <span role="alert" style={{ color: token.danger, fontSize: "var(--nv-fs-label)" }}>
                {t("finance.fid.palier.none")}
              </span>
            )}
            {touched && palierCodeMissing && (
              <span role="alert" style={{ color: token.danger, fontSize: "var(--nv-fs-label)" }}>
                {t("finance.fid.palier.codeRequired")}
              </span>
            )}
            <div style={{ marginTop: "var(--nv-s-3)" }}>
              <Button variant="secondary" size="sm" disabled={!canManage} onClick={addPalier}>
                + {t("finance.fid.palier.add")}
              </Button>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={save} loading={putMut.isPending} disabled={!canManage}>
              {t("finance.fid.bareme.save")}
            </Button>
          </div>
        </div>
      </Card>

      <Card title={t("finance.fid.chart.title")}>
        {chartData.length > 0 ? (
          <div style={{ width: "100%", height: 260 }} aria-hidden>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={token.lineFaint} vertical={false} />
                <XAxis dataKey="code" tick={{ fontSize: 12, fill: token.slate }} stroke={token.line} />
                <YAxis
                  yAxisId="seuil"
                  tick={{ fontSize: 11, fill: token.slate }}
                  stroke={token.line}
                  width={64}
                />
                <YAxis
                  yAxisId="remise"
                  orientation="right"
                  tick={{ fontSize: 11, fill: token.slate }}
                  stroke={token.line}
                  width={44}
                  unit="%"
                />
                <Tooltip
                  contentStyle={{
                    background: token.surface,
                    border: `1px solid ${token.line}`,
                    borderRadius: "8px",
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar
                  yAxisId="seuil"
                  dataKey="seuil"
                  name={t("finance.fid.chart.seuil")}
                  fill={token.azure}
                  radius={[4, 4, 0, 0]}
                />
                <Line
                  yAxisId="remise"
                  type="monotone"
                  dataKey="remise"
                  name={t("finance.fid.chart.remise")}
                  stroke={token.success}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        ) : null}
        <ul className="nv-sr-only">
          {chartData.map((d) => (
            <li key={d.code}>
              {d.code}: {t("finance.fid.chart.seuil")} {d.seuil} · {t("finance.fid.chart.remise")} {d.remise}%
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
